import { useState } from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/Sheet'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/AlertDialog'
import { VOCABULARY_CATEGORY_LABELS } from '@/constants/VocabularyLabels'
import type { VocabularyEntry } from '@/db/Types'
import './vocabulary-detail-sheet.css'

interface VocabularyDetailSheetProps {
	entry: VocabularyEntry | null
	open: boolean
	onOpenChange: (open: boolean) => void
	onUpdateCode: (id: string, newCode: string) => Promise<void>
	onDelete: (id: string) => Promise<void>
	onEditEntity: (entry: VocabularyEntry) => void
}

export function VocabularyDetailSheet({ entry, open, onOpenChange, onUpdateCode, onDelete, onEditEntity }: VocabularyDetailSheetProps) {
	const [editingCode, setEditingCode] = useState(false)
	const [codeValue, setCodeValue] = useState('')

	if (!entry) return null

	function startEditCode() {
		setCodeValue(entry!.code)
		setEditingCode(true)
	}

	async function saveCode() {
		if (codeValue.trim() && codeValue !== entry!.code) {
			await onUpdateCode(entry!.id, codeValue.trim())
		}
		setEditingCode(false)
	}

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="vocabulary-detail-sheet">
				<SheetHeader>
					<SheetTitle>{entry.display_name ?? entry.code}</SheetTitle>
					<SheetDescription>View and edit vocabulary entry details</SheetDescription>
				</SheetHeader>

				<div className="vocabulary-detail-sheet__body">
					<div className="vocabulary-detail-sheet__preview">
						{/* Entity preview placeholder — will show formation/concept thumbnail */}
					</div>

					<div className="vocabulary-detail-sheet__field">
						<label className="vocabulary-detail-sheet__label">Code</label>
						{editingCode ? (
							<div className="vocabulary-detail-sheet__code-edit">
								<Input
									value={codeValue}
									onChange={(e) => setCodeValue(e.target.value)}
									onKeyDown={(e) => { if (e.key === 'Enter') saveCode(); if (e.key === 'Escape') setEditingCode(false) }}
									onBlur={saveCode}
									autoFocus
								/>
							</div>
						) : (
							<button className="vocabulary-detail-sheet__code-display" onClick={startEditCode} type="button">
								<span className="vocabulary-detail-sheet__code-value">{entry.code}</span>
								<span className="vocabulary-detail-sheet__code-edit-hint">click to edit</span>
							</button>
						)}
					</div>

					<div className="vocabulary-detail-sheet__field">
						<label className="vocabulary-detail-sheet__label">Category</label>
						<span className="vocabulary-detail-sheet__badge" data-category={entry.category}>
							{VOCABULARY_CATEGORY_LABELS[entry.category] ?? entry.category}
						</span>
					</div>

					{entry.auto_generated && (
						<p className="vocabulary-detail-sheet__auto-hint">Code was auto-generated from name</p>
					)}
				</div>

				<SheetFooter className="vocabulary-detail-sheet__footer">
					<Button variant="action" onClick={() => onEditEntity(entry)}>
						Edit Entity
					</Button>
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button variant="destructive">Delete</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Delete vocabulary entry?</AlertDialogTitle>
								<AlertDialogDescription>
									This will remove the code &ldquo;{entry.code}&rdquo; from your vocabulary. The underlying {entry.entity_type} will not be deleted.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction onClick={() => onDelete(entry.id)}>Delete</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</SheetFooter>
			</SheetContent>
		</Sheet>
	)
}
